/* istanbul ignore file */
import wishlistStore from '@common-vue/product-thumbnail/src/store_modules/wishlistManager/store';
import modalOverlayStore from '@common-vue/atomic-ui/src/store_modules/modalOverlay/store';
import visualFilterStore from '@common-vue/canvas-aem/src/store_modules/visualFilter/store';
import recommendationStore from '@common-vue/pros-collection/src/store_modules/store';
import imageMapStore from '@common-vue/canvas-aem/src/store_modules/imageMap/store';
import googleAdStore from '@common-vue/site-monetization/src/modules/googleAds/store';
import sponsoredItemsStore from '@common-vue/site-monetization/src/modules/sponsoredItems/store';
import productQuickviewStore from '@common-vue/product-quickview/src/store/productQuickviewStore';
import traitsStore from '@common-vue/media-card-large/src/store_modules/traitsStore';
import slideoutStore from '@common-vue/atomic-ui/src/store_modules/slideout/store';
import { ref } from 'vue';
import { locations } from '@common-vue/locator/src/store';
import encodeSearchKeyword from '@utils/encodeSearchKeyword';
import combineExperimentIds from '@utils/combineExperimentIds';
import FilteringFacetsStore from '@components/FilteringFacets/store';
import { brand } from 'config';

const storeRowSortableGrid = ref(null);

export default {
  namespaced: true,
  state() {
    return {
      pagination: [
        {
          key: 'Sortby',
          value: 'ORIGINAL',
        },
        {
          key: 'Pageindex',
          value: 1,
        },
        {
          key: 'Productsperpage',
          value: 60,
        },
      ],
      pzoEligibleItems: {},
      sortableGrid: {
        imageHoveringData: null,
        selectedColor: null,
      },
      altGridViewProdsPerRow: null,
    };
  },
  getters: {
    getRowSortableGrid(state) {
      if (storeRowSortableGrid.value) {
        return storeRowSortableGrid;
      }
      const localGrid = state.pageData?.body?.canvas?.rows ? state.pageData.body.canvas.rows
        .find((item) => !!item.rowSortableGrid)
        ?.rowSortableGrid : undefined;

      storeRowSortableGrid.value = localGrid;

      return storeRowSortableGrid;
    },
    getSortableGrid(state, getters) {
      return getters.getRowSortableGrid?.value?.zones?.[1]?.sortableGrid ?? {};
    },
    getCategoryTree(state, getters) {
      return getters.getRowSortableGrid?.value?.zones?.[0]?.categoryTree ?? getters.getPage?.body?.categoryTree ?? {};
    },
    getFacets(state, getters) {
      return getters.getRowSortableGrid?.value?.zones?.[0]?.facets?.facets || [];
    },
    getSelectedFacets(state, getters) {
      return getters.getRowSortableGrid?.value?.zones?.[0]?.facets?.meta?.selectedFacets || {};
    },
    getFacetsSelected(state, getters) {
      return getters.getRowSortableGrid?.value?.zones?.[0]?.facets?.meta?.selected?.reduce((selectedValues, facet) => {
        selectedValues[facet.name] = facet.values.map((item) => item.value);
        return selectedValues;
      }, {}) || [];
    },
    getMetaProperties(state, getters) {
      return getters?.getMeta?.properties ?? {};
    },
    getFacetMetaBaseUrl(state, getters) {
      const uniqueSearchPatternEnabled = getters.getMetaProperties?.uniqueSearchPatternEnabled;
      const facetMeta = getters.getRowSortableGrid?.value?.zones?.[0]?.facets?.meta;
      const metaBaseUrl = facetMeta?.baseUrl;

      // for both brands
      const isSearchToFeatured = uniqueSearchPatternEnabled && metaBaseUrl?.startsWith('/shop/search');
      const isSearch = state.pageData?.meta?.pageType?.search;

      let baseUrl = metaBaseUrl;
      if (isSearch && isSearchToFeatured) {
        const queryParams = new URLSearchParams(facetMeta?.queryString || '');
        const keyword = queryParams.get('keyword');
        const encoded = encodeSearchKeyword(keyword, brand === 'bcom');
        baseUrl = `/shop/featured/${encoded}`;
      }

      return baseUrl;
    },
    getSelectedFacetUrl(state, getters) {
      let selectedFacetUrl = getters.getRowSortableGrid?.value?.zones?.[0]?.facets?.meta?.selectedFacetUrl;
      if (typeof selectedFacetUrl === 'string') {
        selectedFacetUrl = selectedFacetUrl.replace(/\|/g, '%7C');
      }
      return selectedFacetUrl;
    },
    getPage(state) {
      return state.pageData;
    },
    getAnalyticsData(state) {
      return state.pageData?.meta?.analytics?.data;
    },
    getModel(state, getters) {
      return getters.getSortableGrid?.model ?? {};
    },
    getProductCount(state, getters) {
      return getters.getRowSortableGrid?.value?.zones?.[0].facets?.meta?.productCount || 0;
    },
    getDisableDeliveryAndFulfillmentFacet(state, getters) {
      return getters.getMeta?.pageType?.disableDeliveryAndFulfillmentFacet || false;
    },
    getSelectedFacetCount(state, getters) {
      const selectedFacets = getters.getSelectedFacets;
      return Object.values(selectedFacets).flatMap?.((values) => values)?.length || 0;
    },
    getFacetsMeta(state, getters) {
      return getters.getRowSortableGrid?.value?.zones?.[0].facets?.meta;
    },
    getSortOptions(state, getters) {
      const model = getters.getModel || null;
      let selected = null;
      let options = [];
      if (!model || !model.sort) return { options, selected };
      options = model.sort.map(({ value, name, isSelected }) => {
        const option = { value, displayValue: name };
        if (isSelected) {
          selected = option;
        }

        return option;
      });
      return { options, selected };
    },
    getSortOptionsForFacets(state, getters) {
      const model = getters.getModel || null;
      const selectedSortOption = model.sort?.map((item) => ({
        ...item,
        displayValue: item.name,
      }));
      return selectedSortOption;
    },
    getCurrentSortby(state) {
      const pageSortby = state?.pagination?.find((item) => item.key === 'Sortby');
      return pageSortby?.value ?? 'ORIGINAL';
    },
    getCurrentPageindex(state) {
      const pagePageindex = state?.pagination?.find((item) => item.key === 'Pageindex');
      return pagePageindex?.value ?? 1;
    },
    getCurrentProductsperpage() {
      return 60;
    },
    getPathUrls(state) {
      const result = state.pagination
        .filter((item) => !(
          (item.key === 'Sortby' && item.value === 'ORIGINAL')
        || (item.key === 'Pageindex' && item.value === 1)
        || (item.key === 'Productsperpage' && item.value === 60)
        ))
        .map(({ key, value }) => ({ key, value }));
      return result;
    },
    getPagination(state, getters) {
      const model = getters.getModel || null;
      let pageIndex = getters.getCurrentPageindex;
      const perPage = getters.getCurrentProductsperpage;
      let sortBy = getters.getCurrentSortby;
      let selected = null;

      if (!model || !model.pagination) return { pageIndex, perPage, sortBy };
      if (brand === 'bcom' && model.pagination?.numbers?.length) {
        const currentNumber = model.pagination.numbers.find(
          (num) => num.current && (num.url.includes('Upc_bops_purchasable') || num.url.includes('Upc_sdd_purchasable')),
        )?.name;

        model.pagination.currentPage = Number(currentNumber ?? model.pagination.numbers.find((num) => num.current)?.name) || 0;
      }

      pageIndex = model.pagination.currentPage === 0 ? pageIndex : model.pagination.currentPage ?? 1;
      selected = model.sort.find(({ isSelected }) => isSelected);
      sortBy = selected ? selected.value : 'ORIGINAL';

      return { pageIndex, perPage, sortBy };
    },
    getPzoItems(state) {
      return state?.pzoEligibleItems?.List?.map((x) => x.productId) || [];
    },
    getProductIds(state, getters) {
      const collection = getters.getCollection ?? [];
      const productIds = [];
      collection.forEach((val) => {
        productIds.push(val.product?.id);
      });
      return productIds;
    },
    getMeta(state) {
      return state.pageData?.meta;
    },
    getCollection(state, getters) {
      if (state.pageData?.redirect) {
        return [];
      }
      return getters?.getSortableGrid?.collection ?? [];
    },
    getIsDomestic(state) {
      return state.pageData.isDomestic;
    },
    getExperimentIds(state) {
      const sourceExperimentIds = state.pageData?.meta?.context?.customerExperiment?.split(',').map((id) => id.trim()) || [];
      const SSCExperimentIds = state.pageData?.SSCExperimentIds || [];
      return combineExperimentIds(sourceExperimentIds, SSCExperimentIds);
    },
    getConvoSearchResult(state) {
      return state.pageData.body.conversationalSearchResult;
    },
    getConversationId(_, getters) {
      return getters.getConvoSearchResult?.conversationId;
    },
    getAltGridViewProdsPerRow(state) {
      return state.altGridViewProdsPerRow;
    },
  },
  mutations: {
    SET_PAGE(state, payload) {
      state.pageData = { ...state.pageData, ...payload };
      state.pagination = payload.pagination ?? state.pagination;
      storeRowSortableGrid.value = null;
    },
    SET_PATHURLS(state, payload) {
      payload.forEach(({ key, value }) => {
        const index = state.pagination.findIndex((item) => item.key.toLowerCase() === key.toLowerCase());
        if (index !== -1) {
          state.pagination[index].value = value;
        }
      });
    },
    SET_PZOURL(state, payload) {
      state.pzoEligibleItems = payload;
    },
    RESET_CONVO_SEARCH_RESULT(state) {
      state.pageData.body.conversationalSearchResult = null;
    },
    SET_IMAGE_HOVERING_DATA(state, payload) {
      state.sortableGrid.imageHoveringData = payload;
    },
    SET_SELECTED_COLOR(state, payload) {
      state.sortableGrid.selectedColor = payload;
    },
    SET_ALT_GRID_VIEW_PRODUCTS(state, payload) {
      state.altGridViewProdsPerRow = payload;
    },
    SET_PAGINATION_REFRESH(state, payload) {
      // reset state.pagination if refreshed
      const { pageIndex, perPage, sortBy } = payload;
      const pagination = [{ key: 'Sortby', value: sortBy ?? 'ORIGINAL' }, { key: 'Pageindex', value: pageIndex ?? 1 }, { key: 'Productsperpage', value: perPage ?? 60 }];
      state.pagination = pagination;
    },
  },
  actions: {
    SET_PAGE({ commit }, payload) {
      commit('SET_PAGE', payload);
    },
    SET_PATHURLS({ commit }, payload) {
      commit('SET_PATHURLS', payload);
    },
  },
  modules: {
    visualFilterStore,
    modalOverlayStore,
    recommendationStore,
    FilteringFacetsStore,
    wishlistStore,
    googleAdStore,
    sponsoredItemsStore,
    imageMapStore,
    slideoutStore,
    // ProductQuickview
    productQuickviewStore,
    traitsStore,
    locations,
  },
};
